import { useQuery } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import { briefKeys } from "../queryKeys";
import { fetchBrief } from "../repository";
import type { BriefVersion } from "../types";
import { useBriefQueriesEnabled } from "./useBrief";
import { useBriefVersions } from "./useBriefData";
import { useBriefSchemaAvailable } from "./useBriefSchema";

function briefFolderId(ready: boolean, folderId: number | null | undefined): string | null {
  return ready && folderId != null ? String(folderId) : null;
}

/**
 * Which of a project's folders is its Brief folder, and the brief's confirmed
 * versions — what /documents needs to show that folder as the brief: its name
 * and icon, and its versions under its row. Nothing for a project with no
 * brief, a viewer without VIEW_BRIEFS, or before the brief schema is deployed.
 *
 * Reads the Brief page's own cache entries (`briefKeys.root` / `.versions`,
 * same fetchers, same shapes), so the two pages never fetch twice.
 */
export function useProjectBriefFolder(projectId: number | null): {
  folderId: string | null;
  versions: BriefVersion[];
} {
  const perms = usePermissions();
  const allowed = projectId !== null && perms.canViewBriefsOnProject(projectId);
  const enabled = useBriefQueriesEnabled(allowed ? projectId : null);
  const { available } = useBriefSchemaAvailable(enabled);
  const ready = enabled && available === true;

  const brief = useQuery({
    queryKey: briefKeys.root(projectId),
    queryFn: () => fetchBrief(projectId as number),
    enabled: ready,
  });
  const briefId = ready ? (brief.data?.id ?? null) : null;
  const { versions } = useBriefVersions(briefId, ready);

  return {
    folderId: briefFolderId(ready, brief.data?.folder_id),
    versions: briefId === null ? [] : versions,
  };
}
